import { CtaCard } from '@landtrustinc/design-system';
import React, { useEffect } from 'react';

import {
  getLtPlusVariantData,
  LT_PLUS_EXISTING_MEMBER_CONFIRMATION,
} from '../../config/ltPlusVariants';
import type { CurrentUser, LtPlusCta, LtPlusCtaMessage } from '../../types';
import { ANALYTICS_KEYS, BUCK_EVENTS } from '../../utils/analytics';

type LtPlusCtaProps = {
  msg: LtPlusCtaMessage;
  listingId?: string | null;
  packageId?: string | null;
  onAnalyticsEvent?: (event: string, data: Record<string, unknown>) => void;
  /** If provided, opens drawer instead of navigating to URL */
  onCtaClick?: (ltPlusCta: LtPlusCta) => void;
  currentUser?: CurrentUser;
};

const LtPlusCtaComponent: React.FC<LtPlusCtaProps> = ({
  msg,
  listingId,
  packageId,
  onAnalyticsEvent,
  onCtaClick,
}) => {
  const ltPlusCta = msg.ltPlusCta;
  const variantData = getLtPlusVariantData(ltPlusCta?.variant);
  const showConfirmation = msg.showConfirmation ?? false;
  const isExistingMemberConfirmation = msg.ltPlusConfirmationKind === 'existing-member';

  // Fire display analytics on mount
  useEffect(() => {
    if (ltPlusCta) {
      onAnalyticsEvent?.(BUCK_EVENTS.LT_PLUS_CTA_DISPLAYED, {
        [ANALYTICS_KEYS.LISTING_ID]: listingId,
        [ANALYTICS_KEYS.PACKAGE_ID]: packageId || 'N/A',
        [ANALYTICS_KEYS.VARIANT]: ltPlusCta.variant,
        [ANALYTICS_KEYS.MESSAGE_ID]: msg.id,
      });
    }
  }, [ltPlusCta, listingId, packageId, onAnalyticsEvent]);

  if (!ltPlusCta) {
    return null;
  }

  const handleClick = () => {
    // Fire analytics event when CTA is clicked
    onAnalyticsEvent?.(BUCK_EVENTS.LT_PLUS_CTA_CLICKED, {
      [ANALYTICS_KEYS.LISTING_ID]: listingId,
      [ANALYTICS_KEYS.PACKAGE_ID]: packageId || 'N/A',
      [ANALYTICS_KEYS.VARIANT]: ltPlusCta.variant,
      [ANALYTICS_KEYS.MESSAGE_ID]: msg.id,
    });

    // If callback provided, open drawer instead of navigating
    if (onCtaClick) {
      onCtaClick(ltPlusCta);
    }
  };

  // If onCtaClick is provided, render a clickable card that opens the drawer
  if (onCtaClick) {
    if (showConfirmation) {
      const title = isExistingMemberConfirmation
        ? LT_PLUS_EXISTING_MEMBER_CONFIRMATION.header
        : variantData.confirmationHeader;
      const description = isExistingMemberConfirmation
        ? LT_PLUS_EXISTING_MEMBER_CONFIRMATION.subtext
        : variantData.confirmationSubtext;

      return <CtaCard key={msg.id} title={title ?? ''} description={description ?? ''} />;
    }

    return (
      <CtaCard
        key={msg.id}
        title={variantData.header ?? ''}
        description={variantData.subtext ?? ''}
        ctaLabel={variantData.ctaLabel ?? 'Learn More'}
        onCtaClick={handleClick}
      />
    );
  }
};

export default LtPlusCtaComponent;
